import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, rp, UNIT_STATUSES } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Copy, Pencil, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { AssetToolbar } from "@/admin/AssetToolbar";
import { logError } from "@/lib/log";

const sel = "w-full h-10 rounded-md border border-input bg-white px-3 text-sm";

const SCHEMAS = {
  packages: {
    title: "Rental Packages", endpoint: "packages", ordered: true,
    fields: [
      ["name", "Package Name", "text"], ["duration_type", "Duration Type", "select", ["hour", "day", "weekday_block", "overnight", "custom"]],
      ["duration_value", "Duration Value", "number"],
      ["duration_display", "Label Durasi di Publik (mis. 4 Hari Kalender)", "text"],
      ["normal_price", "Harga Coret (pembanding)", "number"],
      ["promo_price", "Harga Jual", "number"], ["promo_start", "Promo Start Date", "date"],
      ["promo_end", "Promo End Date", "date"], ["badge", "Badge", "text"],
      ["description", "Description", "textarea"], ["display_order", "Display Order", "number"],
      ["active", "Active", "bool"], ["visible", "Visible to Customers", "bool"],
      ["service_policy", "Layanan Paket Ini", "select", ["customer_arranged", "lapak_delivery"]],
    ],
    columns: [["name", "Name"], ["duration", "Duration"], ["normal_price", "Normal", "money"], ["promo_price", "Promo", "money"],
      ["badge", "Badge"], ["service_policy", "Layanan"], ["active", "Active", "bool"], ["visible", "Visible", "bool"], ["display_order", "Order"]],
    defaults: { name: "", duration_type: "hour", duration_value: 1, normal_price: 0, promo_price: null, promo_start: null, promo_end: null, badge: "", description: "", display_order: 99, active: true, visible: true, service_policy: "customer_arranged" },
  },
  units: {
    title: "Rental Units", endpoint: "units",
    fields: [["unit_code", "Asset ID (dibuat otomatis: LM-JKT-001)", "text"],
      ["table_color", "Warna Meja", "select", ["Black", "White", "Unknown"]],
      ["model", "Model", "text"],
      ["branch_id", "Hub saat ini", "branch"],
      ["tile_type", "Tile Type", "select", ["Regular", "Premium"]], ["status", "Status", "select", UNIT_STATUSES],
      ["purchase_date", "Purchase Date", "date"], ["current_location", "Current Location", "text"], ["notes", "Notes", "textarea"]],
    columns: [["unit_code", "Asset ID"], ["table_color", "Warna"], ["branch_id", "Hub", "branch"], ["tile_type", "Tile Type"], ["status", "Status"], ["total_rentals", "Rentals"], ["current_location", "Location"]],
    defaults: { unit_code: "", table_color: "Unknown", model: "", tile_type: "Regular", status: "Available", purchase_date: null, current_location: "", notes: "", total_rentals: 0 },
  },
  "delivery-areas": {
    title: "Delivery Areas", endpoint: "delivery-areas", ordered: true,
    fields: [["area_name", "Area Name", "text"], ["distance_km", "Distance from HQ (km)", "number"],
      ["bike_fee", "Bike Fee Override (0 = auto Lalamove estimate)", "number"],
      ["car_fee", "Car Fee Override (0 = auto Lalamove estimate)", "number"],
      ["display_order", "Display Order", "number"], ["active", "Active", "bool"]],
    columns: [["area_name", "Area"], ["distance_km", "Distance (km)"], ["bike_fee", "Bike Fee", "money"],
      ["car_fee", "Car Fee", "money"], ["active", "Active", "bool"]],
    defaults: { area_name: "", distance_km: 10, bike_fee: null, car_fee: null, delivery_fee: 0, pickup_fee: 0, display_order: 99, active: true },
  },
  addons: {
    title: "Add-ons", endpoint: "addons",
    fields: [["name", "Name", "text"], ["description", "Description", "textarea"], ["price", "Price", "number"],
      ["price_type", "Price Type", "select", ["per_booking", "per_unit", "per_hour", "custom"]], ["active", "Active", "bool"],
      ["public_visible", "Tampil di checkout publik?", "bool"],
      ["requires_admin_confirmation", "Butuh konfirmasi Admin?", "bool"]],
    columns: [["name", "Name"], ["price", "Price", "money"], ["price_type", "Price Type"], ["active", "Active", "bool"], ["public_visible", "Publik", "bool"]],
    defaults: { name: "", description: "", price: 0, price_type: "per_booking", active: true, public_visible: true, requires_admin_confirmation: false },
  },
  promos: {
    title: "Promotions", endpoint: "promos",
    fields: [["promo_code", "Promo Code", "text"], ["promo_name", "Promo Name", "text"],
      ["discount_type", "Discount Type", "select", ["percentage", "fixed", "free_delivery"]], ["discount_amount", "Discount Amount (kosongkan untuk free_delivery)", "number"],
      ["minimum_spend", "Minimum Spend", "number"], ["max_discount", "Maximum Discount", "number"],
      ["start_date", "Start Date", "date"], ["end_date", "End Date", "date"],
      ["usage_limit", "Maximum Usage (0 = unlimited)", "number"], ["usage_per_customer", "Usage Per Customer (0 = unlimited)", "number"],
      ["applies_to_packages", "Valid Only For These Packages (none selected = all packages)", "packages"],
      ["applies_to_all_hubs", "Berlaku di semua Hub (termasuk Hub baru nanti)?", "bool"],
      ["applies_to_hubs", "Kalau tidak: pilih Hub yang boleh pakai promo ini", "hubs"],
      ["allow_member_discount", "Boleh digabung diskon member?", "bool"],
      ["member_discount_base", "Diskon member dihitung dari", "select", ["", "normal", "promo"]],
      ["active", "Active", "bool"]],
    columns: [["promo_code", "Code"], ["promo_name", "Name"], ["discount_type", "Type"], ["discount_amount", "Amount"],
      ["applies_to_packages", "Packages", "packages"], ["applies_to_hubs", "Hub", "hubs"],
      ["allow_member_discount", "+Member", "bool"],
      ["used_count", "Used"], ["usage_limit", "Limit"], ["active", "Active", "bool"]],
    defaults: { promo_code: "", promo_name: "", discount_type: "percentage", discount_amount: 10, minimum_spend: 0, max_discount: 0, start_date: null, end_date: null, usage_limit: 0, usage_per_customer: 0, used_count: 0, applies_to_packages: [], applies_to_all_hubs: true, applies_to_hubs: [], allow_member_discount: true, member_discount_base: "", active: true },
  },
};

export default function Resources({ kind }) {
  const S = SCHEMAS[kind];
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [allPackages, setAllPackages] = useState([]);
  const [branches, setBranches] = useState([]);
  const load = () => api.get(`/${S.endpoint}?all=1`).then((r) => setRows(r.data));
  useEffect(() => {
    load();
    if (kind === "promos") api.get("/packages?all=1").then((r) => setAllPackages(r.data)).catch((e) => logError("Failed to load packages", e));
    if (kind === "promos") api.get("/branches").then((r) => setBranches(r.data)).catch(() => setBranches([]));
    if (kind === "units") api.get("/branches").then((r) => setBranches(r.data)).catch((e) => logError("Failed to load branches", e));
    // eslint-disable-next-line
  }, [kind]);
  const pkgNames = (ids) => (ids || []).map((id) => allPackages.find((p) => p.id === id)?.name || id);
  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

  const save = async () => {
    const body = { ...edit };
    const isNew = !body.id;
    delete body.id;
    try {
      if (isNew) await api.post(`/${S.endpoint}`, body);
      else await api.put(`/${S.endpoint}/${edit.id}`, body);
      setEdit(null); load(); toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete this item?`)) return;
    try {
      await api.delete(`/${S.endpoint}/${r.id}`);
      load();
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus item");
    }
  };
  const duplicate = async (r) => {
    const copy = { ...r, id: undefined };
    delete copy.id;
    if (copy.name) copy.name = `${copy.name} (Copy)`;
    if (copy.unit_code) copy.unit_code = `${copy.unit_code}-COPY`;
    if (copy.promo_code) copy.promo_code = `${copy.promo_code}2`;
    await api.post(`/${S.endpoint}`, copy); load(); toast.success("Duplicated");
  };
  const move = async (r, dir) => {
    await api.put(`/${S.endpoint}/${r.id}`, { display_order: (r.display_order || 0) + dir });
    load();
  };
  const toggle = async (r, field) => { await api.put(`/${S.endpoint}/${r.id}`, { [field]: !r[field] }); load(); };

  const cell = (r, [key, , type]) => {
    if (key === "duration") return `${r.duration_value} ${r.duration_type}`;
    if (key === "roundtrip") return rp((r.delivery_fee || 0) + (r.pickup_fee || 0));
    if (type === "packages") {
      const names = pkgNames(r[key]);
      return names.length ? <span className="text-xs">{names.join(", ")}</span> : <span className="text-xs text-ink-soft">All packages</span>;
    }
    if (type === "hubs") {
      if (r.applies_to_all_hubs !== false) return <span className="text-xs text-ink-soft">Semua Hub</span>;
      const names = (r[key] || []).map((id) => branchName(id));
      return <span className="text-xs">{names.length ? names.join(", ") : "—"}</span>;
    }
    if (type === "branch") return <span className="text-xs">{branchName(r[key])}</span>;
    const v = r[key];
    if (type === "money") return v == null || v === 0 ? "—" : rp(v);
    if (type === "bool") return (
      <button onClick={() => toggle(r, key)} data-testid={`toggle-${key}-${r.id}`}
        className={`text-[11px] px-2 py-0.5 rounded-full border ${v ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-neutral-100 text-neutral-600 border-neutral-200"}`}>
        {v ? "Yes" : "No"}</button>
    );
    return v ?? "—";
  };

  return (
    <div data-testid={`admin-${kind}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-display text-3xl tracking-tight">{S.title}</h1>
        {kind !== "units" && (
          <button data-testid="create-btn" onClick={() => setEdit({ ...S.defaults })}
            className="lm-btn-primary rounded-md px-4 py-2.5 text-sm font-semibold">+ New</button>
        )}
      </div>
      {kind === "units" && <AssetToolbar rows={rows} onChanged={load} />}
      <div className="mt-5 overflow-x-auto bg-white border border-black/10 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-cream-dark text-left text-[11px] uppercase tracking-widest text-ink-soft">
            <tr>{S.columns.map((c) => <th key={c[0]} className="p-3">{c[1]}</th>)}<th className="p-3">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((r) => (
              <tr key={r.id} data-testid={`row-${r.id}`} className="hover:bg-cream-dark/40">
                {S.columns.map((c) => <td key={c[0]} className="p-3">{cell(r, c)}</td>)}
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    {kind === "units" && (
                      <Link to={`/admin/units/${r.id}`} data-testid={`profile-${r.unit_code}`} className="text-xs text-lm-red hover:underline">Profile</Link>
                    )}
                    <button data-testid={`edit-${r.id}`} onClick={() => setEdit({ ...r })} className="text-ink-soft hover:text-lm-red"><Pencil size={15} /></button>
                    <button data-testid={`dup-${r.id}`} onClick={() => duplicate(r)} className="text-ink-soft hover:text-lm-red"><Copy size={15} /></button>
                    {S.ordered && <>
                      <button onClick={() => move(r, -1)} className="text-ink-soft hover:text-lm-red"><ArrowUp size={15} /></button>
                      <button onClick={() => move(r, 1)} className="text-ink-soft hover:text-lm-red"><ArrowDown size={15} /></button>
                    </>}
                    <button data-testid={`del-${r.id}`} onClick={() => remove(r)} className="text-ink-soft hover:text-lm-red"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit?.id ? "Edit" : "New"} — {S.title}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid sm:grid-cols-2 gap-4">
              {S.fields.map(([key, label, type, opts]) => (
                <label key={key} className={type === "textarea" || type === "packages" || type === "hubs" ? "sm:col-span-2 block" : "block"}>
                  <span className="text-[11px] uppercase tracking-widest text-ink-soft">{label}</span>
                  <div className="mt-1">
                    {type === "hubs" ? (
                      <div className="flex flex-wrap gap-2" data-testid={`field-${key}`}>
                        {edit.applies_to_all_hubs !== false ? (
                          <span className="text-xs text-ink-soft">Promo berlaku di semua Hub — matikan sakelar di atas untuk membatasi.</span>
                        ) : branches.map((b) => {
                          const on = (edit[key] || []).includes(b.id);
                          return (
                            <button key={b.id} type="button" data-testid={`promo-hub-${b.id}`}
                              onClick={() => setEdit({ ...edit, [key]: on ? (edit[key] || []).filter((x) => x !== b.id) : [...(edit[key] || []), b.id] })}
                              className={`text-xs px-3 py-1.5 rounded-full border ${on ? "bg-lm-red text-white border-lm-red" : "border-black/15"}`}>
                              {b.name}
                            </button>
                          );
                        })}
                      </div>
                    ) : type === "packages" ? (
                      <div className="flex flex-wrap gap-2" data-testid={`field-${key}`}>
                        {allPackages.map((p) => {
                          const on = (edit[key] || []).includes(p.id);
                          return (
                            <button key={p.id} type="button" data-testid={`promo-pkg-${p.id}`}
                              onClick={() => setEdit({ ...edit, [key]: on ? (edit[key] || []).filter((x) => x !== p.id) : [...(edit[key] || []), p.id] })}
                              className={`text-xs px-3 py-1.5 rounded-full border ${on ? "bg-lm-red text-white border-lm-red" : "border-black/15"}`}>
                              {p.name}
                            </button>
                          );
                        })}
                        {(edit[key] || []).length === 0 && <span className="text-xs text-ink-soft self-center">No selection = valid for all packages</span>}
                      </div>
                    ) : type === "branch" ? (
                      <select data-testid={`field-${key}`} className={sel} value={edit[key] ?? ""} onChange={(e) => setEdit({ ...edit, [key]: e.target.value })}>
                        <option value="">— pilih cabang —</option>
                        {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </select>
                    ) : type === "select" ? (
                      <select data-testid={`field-${key}`} className={sel} value={edit[key] ?? ""} onChange={(e) => setEdit({ ...edit, [key]: e.target.value })}>
                        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : type === "bool" ? (
                      <button data-testid={`field-${key}`} onClick={() => setEdit({ ...edit, [key]: !edit[key] })}
                        className={`px-4 py-2 rounded-md border text-sm ${edit[key] ? "bg-emerald-50 border-emerald-300" : "border-black/15"}`}>
                        {edit[key] ? "Yes" : "No"}</button>
                    ) : type === "textarea" ? (
                      <Textarea data-testid={`field-${key}`} rows={2} value={edit[key] ?? ""} onChange={(e) => setEdit({ ...edit, [key]: e.target.value })} />
                    ) : key === "unit_code" && kind === "units" ? (
                      <Input
                        data-testid={`field-${key}`}
                        type={type}
                        value={edit[key] ?? ""}
                        readOnly
                        className="bg-neutral-50 text-ink-soft"
                      />
                    ) : (
                      <Input data-testid={`field-${key}`} type={type} value={edit[key] ?? ""}
                        onChange={(e) => setEdit({ ...edit, [key]: type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : (e.target.value || null) })} />
                    )}
                  </div>
                </label>
              ))}
              <div className="sm:col-span-2 flex gap-2">
                <button data-testid="save-btn" onClick={save} className="lm-btn-primary rounded-md px-5 py-2.5 text-sm font-semibold">Save</button>
                <button onClick={() => setEdit(null)} className="rounded-md px-5 py-2.5 text-sm border border-black/15">Cancel</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
